// Group dashboard - /Home/Dashboard. Design: dashboard-multi-branch.html.
//
// One sales-report and one product-wise-sale call per branch the user can see
// (the header's store list), for the period, its comparison and the 12-day
// sparkline window. Profit, collections, purchases and targets come from
// Api/Dashboard/GetSummary for all branches at once; receivables, stock,
// transfers and the alerts from GetPosition / GetList.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { setBranch, setBranchId, setBranchName } from "../../redux/branchlist";
import { fetchAMCstatus } from "../../redux/amcstatus";
import { all_routes } from "../../Router/all_routes";
import {
  Card,
  DashScreen,
  DataTable,
  Delta,
  Drawer,
  Facts,
  Hero,
  LineTrend,
  MicroSpark,
  MiniBtn,
  PERIODS,
  PeriodBar,
  Queue,
  Seg,
  Sparkline,
  StackBar,
  Tile,
  cap,
  cmpRange,
  compact,
  dailyTotals,
  dayLabel,
  daysFor,
  downloadCsv,
  fetchInvoiceSpans,
  fetchItemSales,
  fetchList,
  fetchPosition,
  fetchSummary,
  fmtPct,
  hourLbl,
  hourWindow,
  hourlyTotals,
  inRange,
  listDrawer,
  longDate,
  money,
  moneyDp,
  paceCut,
  pct,
  rangeFor,
  sparkRange,
  sumOf,
  sumPosition,
  useDashKeys,
  useToast,
  useUpdatedLabel,
  valueOf,
} from "../../components/dashboard/ui";

// colour follows the branch (its place in the store list), never its rank
const colourOf = (i) => (i < 6 ? `var(--s${i + 1})` : `hsl(${(i * 67) % 360} 55% 50%)`);

const RANKS = [
  { id: "sales", label: "Sales" },
  { id: "gpPct", label: "Margin" },
  { id: "tgt", label: "Target" },
  { id: "growth", label: "Growth" },
];
const LCOLS = [
  { k: "rank", t: "#", w: 46 },
  { k: "name", t: "Branch" },
  { k: "sales", t: "Net sales", num: true },
  { k: "growth", t: "vs prev", num: true },
  { k: "gpPct", t: "Margin", num: true },
  { k: "inv", t: "Invoices", num: true },
  { k: "avg", t: "Avg bill", num: true },
  { k: "coll", t: "Collected", num: true },
  { k: "ar", t: "Receivable", num: true },
  { k: "stock", t: "Stock value", num: true },
  { k: "tgt", t: "Target", num: true },
  { k: "trend", t: "12-day trend", num: true },
];
const SORTABLE = ["sales", "growth", "gpPct", "inv", "avg", "coll", "ar", "stock", "tgt"];
const AGES = ["Not due / 0–30 days", "31–60 days", "61–90 days", "Over 90 days"];
const ageParts = (p) =>
  ["0To30", "31To60", "61To90", "Over90"].map((k, i) => ({ n: AGES[i], v: p ? Number(p["receivable" + k]) || 0 : 0, c: `var(--age${i + 1})` }));

const GroupDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { BranchByUser } = useSelector((state) => state.userBranch);

  const branches = useMemo(
    () =>
      (Array.isArray(BranchByUser) ? BranchByUser : [])
        .map((o) => o?.branch)
        .filter((b) => b && b.guid)
        .map((b, i) => ({ b, n: b.name || "Branch", c: colourOf(i) })),
    [BranchByUser]
  );
  const branchKey = branches.map((x) => x.b.guid).join("|");

  const [period, setPeriod] = useState("today");
  const [cmp, setCmp] = useState("prev");
  const [vatOn, setVatOn] = useState(true);
  const [rank, setRank] = useState("sales");
  const [off, setOff] = useState({}); // branch guid -> left out of scope
  const [hidden, setHidden] = useState({}); // branch name -> hidden on the chart
  const [tables, setTables] = useState({});
  const [drawer, setDrawer] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stamp, setStamp] = useState(null);
  const [reload, setReload] = useState(0);
  const [toast, showToast] = useToast();
  const updated = useUpdatedLabel(stamp);
  const req = useRef(0);

  /* ---------------- load ---------------- */
  useEffect(() => {
    if (!branches.length) return;
    const id = ++req.current;
    const r = rangeFor(period);
    const c = cmpRange(r, cmp);
    const sp = sparkRange(r);
    setLoading(true);
    setError("");
    const all = branches.map((x) => x.b);
    // group-wide figures - one call each, may fail on their own
    const soft = (p) =>
      p.catch((e) => {
        console.error("Dashboard figure failed", e?.response?.data?.message || e);
        return null;
      });
    Promise.all([
      Promise.all(
        branches.map((x) =>
          Promise.all([fetchInvoiceSpans(x.b, [r, c, sp]), fetchItemSales(x.b, r.from, r.to).catch(() => [])])
            .then(([inv, items]) => ({ guid: x.b.guid, inv, items }))
            .catch((e) => {
              console.error("Dashboard load failed for", x.n, e?.response?.data?.Message || e);
              return { guid: x.b.guid, inv: [], items: [], failed: true };
            })
        )
      ),
      soft(fetchSummary(all, [r, c, sp], r.n === 1)),
      soft(fetchPosition(all)),
      soft(fetchList("price", all)),
      soft(fetchList("misplaced", all)),
    ])
      .then(([list, sum, pos, price, misplaced]) => {
        if (id !== req.current) return;
        const failed = branches.filter((x) => list.find((l) => l.guid === x.b.guid)?.failed).map((x) => x.n);
        const msgs = [];
        if (failed.length) msgs.push(`Could not load ${failed.join(", ")}. Their figures show as zero - press Refresh to retry.`);
        if (!sum || !pos) msgs.push("Some figures (profit, stock, receivables) could not be loaded.");
        setError(msgs.join(" "));
        setData({
          r,
          c,
          sp,
          by: Object.fromEntries(list.map((l) => [l.guid, l])),
          sum,
          pos: Object.fromEntries((pos || []).map((p) => [p.branchID, p])),
          hasPos: !!pos,
          price: price || [],
          misplaced: misplaced || [],
        });
        setStamp(Date.now());
      })
      .finally(() => id === req.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey, period, cmp, reload]);

  const visible = branches.filter((x) => !off[x.b.guid]);

  /* ---------------- derived ---------------- */
  const v = useMemo(() => {
    if (!data) return null;
    const { r, c, sp, by, sum, pos } = data;
    const f = valueOf(vatOn);
    const rows = visible.map((x) => {
      const all = by[x.b.guid]?.inv || [];
      const cur = inRange(all, r.from, r.to);
      const prev = paceCut(inRange(all, c.from, c.to), c.to, r.live);
      const sales = sumOf(cur, f);
      const prevSales = sumOf(prev, f);
      // GetSummary days for this branch
      const sdays = sum ? daysFor(sum.days, x.b.guid) : [];
      const sd = inRange(sdays, r.from, r.to);
      const pd = inRange(sdays, c.from, c.to);
      const s = (k) => sumOf(sd, (d) => d[k]);
      const ps = (k) => sumOf(pd, (d) => d[k]);
      const p = pos[x.b.guid] || null;
      const target = s("target");
      const achieved = s("netSales");
      return {
        sdays,
        p,
        gp: s("grossProfit"),
        pgp: ps("grossProfit"),
        salesEx: s("salesExVat"),
        gpPct: sum ? pct(s("grossProfit"), s("salesExVat")) : -Infinity,
        coll: s("collections"),
        pcoll: ps("collections"),
        purch: s("purchases") - s("purchaseReturns"),
        ppurch: ps("purchases") - ps("purchaseReturns"),
        ar: p ? Number(p.receivableTotal) || 0 : 0,
        stock: p ? Number(p.stockValue) || 0 : 0,
        target,
        achieved,
        // branches without a target rank last
        tgt: target ? pct(achieved, target) : -Infinity,
        sparkGp: dailyTotals(sdays, sp.from, sp.to, (d) => d.grossProfit).map((d) => d.v),
        sparkColl: dailyTotals(sdays, sp.from, sp.to, (d) => d.collections).map((d) => d.v),
        sparkPurch: dailyTotals(sdays, sp.from, sp.to, (d) => d.purchases - d.purchaseReturns).map((d) => d.v),
        ...x,
        all,
        cur,
        prev,
        items: by[x.b.guid]?.items || [],
        sales,
        prevSales,
        growth: prevSales ? ((sales - prevSales) / prevSales) * 100 : 0,
        inv: cur.length,
        pinv: prev.length,
        avg: cur.length ? sales / cur.length : 0,
        spark: dailyTotals(all, sp.from, sp.to, f).map((d) => d.v),
      };
    });
    const total = sumOf(rows, (x) => x.sales);
    rows.forEach((x) => (x.share = pct(x.sales, total)));
    const sparkGroup = rows.length ? rows[0].spark.map((_, i) => sumOf(rows, (x) => x.spark[i])) : [];
    const sparkInv = rows.length
      ? dailyTotals([], sp.from, sp.to, () => 0).map((d) => sumOf(rows, (x) => x.all.filter((i) => i.date === d.date).length))
      : [];
    const g = (k) => sumOf(rows, (x) => x[k]);
    const sparkSum = (k) => (rows.length ? rows[0][k].map((_, i) => sumOf(rows, (x) => x[k][i])) : []);
    return {
      r,
      c,
      f,
      rows,
      total,
      prevTotal: sumOf(rows, (x) => x.prevSales),
      inv: sumOf(rows, (x) => x.inv),
      pinv: sumOf(rows, (x) => x.pinv),
      sparkGroup,
      sparkInv,
      hasSum: !!sum,
      hasPos: data.hasPos,
      gp: g("gp"),
      pgp: g("pgp"),
      salesEx: g("salesEx"),
      coll: g("coll"),
      pcoll: g("pcoll"),
      purch: g("purch"),
      ppurch: g("ppurch"),
      target: g("target"),
      achieved: g("achieved"),
      withTarget: rows.filter((x) => x.target),
      onTarget: rows.filter((x) => x.target && x.achieved >= x.target),
      sparkGp: sparkSum("sparkGp"),
      sparkColl: sparkSum("sparkColl"),
      sparkPurch: sparkSum("sparkPurch"),
      pos: sumPosition(rows.map((x) => x.p).filter(Boolean)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, vatOn, off, branchKey]);

  const league = useMemo(() => {
    if (!v) return [];
    const k = SORTABLE.includes(rank) ? rank : "sales";
    const out = v.rows.slice().sort((a, b) => (b[k] === a[k] ? b.sales - a.sales : b[k] > a[k] ? 1 : -1));
    out.forEach((x, i) => (x.rank = i + 1));
    return out;
  }, [v, rank]);

  /* ---------------- trend ---------------- */
  const trend = useMemo(() => {
    if (!v || !v.rows.length) return null;
    const { r, f, rows } = v;
    if (r.n === 1) {
      const win = hourWindow(rows.map((x) => x.cur), r.live);
      if (win)
        return {
          unit: "hour",
          x: hourlyTotals([], win, f).map((o) => hourLbl(o.h)),
          series: rows.map((x) => ({ n: x.n, c: x.c, v: hourlyTotals(x.cur, win, f).map((o) => o.v) })),
        };
      // the invoices carry bare dates - use the summary's hourly split
      const hrs = (data.sum?.hours || []).filter((o) => o.date === r.to);
      if (hrs.length) {
        const lo = Math.min(...hrs.map((o) => o.h));
        const hi = r.live ? Math.max(dayjs().hour(), ...hrs.map((o) => o.h)) : Math.max(...hrs.map((o) => o.h));
        const hs = [];
        for (let h = lo; h <= hi; h++) hs.push(h);
        return {
          unit: "hour",
          fromSummary: true,
          x: hs.map(hourLbl),
          series: rows.map((x) => ({
            n: x.n,
            c: x.c,
            v: hs.map((h) => sumOf(hrs.filter((o) => o.branch === x.b.guid && o.h === h), (o) => o.v)),
          })),
        };
      }
      const sp = sparkRange(r);
      return {
        unit: "day",
        noTimes: true,
        x: dailyTotals([], sp.from, sp.to, f).map((o) => dayLabel(o.date)),
        series: rows.map((x) => ({ n: x.n, c: x.c, v: x.spark })),
      };
    }
    return {
      unit: "day",
      x: dailyTotals([], r.from, r.to, f).map((o) => dayLabel(o.date)),
      series: rows.map((x) => ({ n: x.n, c: x.c, v: dailyTotals(x.cur, r.from, r.to, f).map((o) => o.v) })),
    };
  }, [v, data]);

  /* ---------------- top items across the group ---------------- */
  const topItems = useMemo(() => {
    if (!v) return [];
    const m = {};
    v.rows.forEach((x) =>
      x.items.forEach((it) => {
        if (!m[it.name]) m[it.name] = { name: it.name, value: 0, qty: 0, by: [] };
        m[it.name].value += it.value;
        m[it.name].qty += it.qty;
        m[it.name].by.push({ n: x.n, value: it.value, qty: it.qty });
      })
    );
    return Object.values(m)
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [v]);

  /* ---------------- drawer ---------------- */
  const drawerCsv = (d) => {
    downloadCsv(
      `${d.title}.csv`.replace(/[\\/:*?"<>|]/g, "-"),
      d.cols.map((c) => c.t),
      d.rows.map((row) => d.cols.map((c) => (typeof row[c.k] === "object" ? "" : row[c.k])))
    );
    showToast("List exported");
  };
  const openBranch = useCallback(
    (x) => {
      if (!v) return;
      const f = v.f;
      const pair = (m, a, b) => ({ m, v: a, p: b });
      const credit = (rows) => sumOf(rows.filter((i) => i.payType === 4), f);
      setDrawer({
        guid: x.b.guid,
        title: x.n,
        sub: `${cap(v.r.name)} vs ${v.c.name}`,
        cols: [
          { k: "m", t: "Measure" },
          { k: "v", t: cap(v.r.name), num: true },
          { k: "p", t: "Comparison", num: true },
        ],
        rows: [
          pair("Net sales", moneyDp(x.sales), moneyDp(x.prevSales)),
          pair("Share of group", fmtPct(x.share), "—"),
          pair("Invoices", money(x.inv), money(x.pinv)),
          pair("Average bill", moneyDp(x.avg), moneyDp(x.pinv ? x.prevSales / x.pinv : 0)),
          pair("Discount given", moneyDp(sumOf(x.cur, (i) => i.discount)), moneyDp(sumOf(x.prev, (i) => i.discount))),
          pair("VAT", moneyDp(sumOf(x.cur, (i) => i.vat)), moneyDp(sumOf(x.prev, (i) => i.vat))),
          pair("Cash", moneyDp(sumOf(x.cur, (i) => i.cash)), moneyDp(sumOf(x.prev, (i) => i.cash))),
          pair("Card", moneyDp(sumOf(x.cur, (i) => i.card)), moneyDp(sumOf(x.prev, (i) => i.card))),
          pair("Credit sales", moneyDp(credit(x.cur)), moneyDp(credit(x.prev))),
          ...(v.hasSum
            ? [
                pair("Gross profit", moneyDp(x.gp), moneyDp(x.pgp)),
                pair("Margin", fmtPct(pct(x.gp, x.salesEx)), "—"),
                pair("Collections", moneyDp(x.coll), moneyDp(x.pcoll)),
                pair("Purchases (net of returns)", moneyDp(x.purch), moneyDp(x.ppurch)),
                pair("Target", x.target ? moneyDp(x.target) : "not set", "—"),
                pair("Target attainment", x.target ? fmtPct(x.tgt) : "—", "—"),
              ]
            : []),
          ...(x.p
            ? [
                pair("Receivable (now)", moneyDp(x.ar), "—"),
                pair("Receivable over 90 days", moneyDp(x.p.receivableOver90), "—"),
                pair("Payable (now)", moneyDp(x.p.payableTotal), "—"),
                pair("Stock value (now)", moneyDp(x.stock), "—"),
                pair("Stock cover", x.p.stockCoverDays ? `${Math.round(x.p.stockCoverDays)} days` : "—", "—"),
                pair("Out of stock items", money(x.p.outOfStockItems), "—"),
              ]
            : []),
        ],
      });
    },
    [v]
  );
  // GetList rows behind an alert, fetched when opened; `only` narrows to one branch
  const listReq = useRef(0);
  const openList = (kind, only) => {
    const id = ++listReq.current;
    const scope = only ? [only.b] : visible.map((x) => x.b);
    const sub = only ? only.n : `${scope.length} branches`;
    setDrawer({ ...listDrawer(kind, [], sub), note: "Loading…" });
    fetchList(kind, scope)
      .then((rows) => id === listReq.current && setDrawer(listDrawer(kind, rows, `${rows.length} rows · ${sub}`)))
      .catch((e) => {
        if (id !== listReq.current) return;
        setDrawer(null);
        showToast(e?.response?.data?.message || "Could not load the list");
      });
  };
  const openInGroupRows = (title, rows) =>
    setDrawer({
      title,
      sub: `${rows.length} rows · ${visible.length} branches`,
      cols: [
        { k: "br", t: "Branch" },
        { k: "bill", t: "Bill no" },
        { k: "d", t: "Date" },
        { k: "amt", t: "Net total", num: true },
      ],
      rows: rows.map((i) => ({ br: i.br, bill: i.billNo || "—", d: dayLabel(i.date), amt: moneyDp(i.net) })),
    });
  const goBranch = (guid) => {
    const x = branches.find((b) => b.b.guid === guid);
    if (!x) return;
    dispatch(fetchAMCstatus(x.b));
    dispatch(setBranchId({ guid: x.b.guid }));
    dispatch(setBranchName({ name: x.n }));
    dispatch(setBranch(x.b));
    setDrawer(null);
    navigate(all_routes.branchdashboard);
  };

  /* ---------------- handlers ---------------- */
  const refresh = () => {
    setReload((x) => x + 1);
    showToast("Figures refreshed");
  };
  const toggleTable = (k) => setTables((t) => ({ ...t, [k]: !t[k] }));
  const toggleBranch = (guid) => {
    if (!off[guid] && visible.length === 1) return showToast("Keep at least one branch in view");
    setOff((o) => ({ ...o, [guid]: !o[guid] }));
  };
  const allBranches = () => setOff({});
  const toggleSeries = (n) => {
    const shownNow = (trend?.series || []).filter((s) => !hidden[s.n]).length;
    if (!hidden[n] && shownNow === 1) return showToast("Keep at least one branch on the chart");
    setHidden((h) => ({ ...h, [n]: !h[n] }));
  };
  const exportCsv = () => {
    downloadCsv(
      `axoone-group-${period}.csv`,
      ["Rank", "Branch", "Net sales", "Growth %", "Invoices", "Avg bill", "Share %"],
      league.map((x) => [x.rank, x.n, x.sales.toFixed(2), x.growth.toFixed(2), x.inv, x.avg.toFixed(2), x.share.toFixed(2)])
    );
    showToast("Branch league exported");
  };

  useDashKeys({ onPeriod: setPeriod, onRefresh: refresh, onEscape: () => setDrawer(null), more: { a: allBranches } });

  /* ---------------- render ---------------- */
  const r = v?.r || rangeFor(period);
  const c = v?.c || cmpRange(r, cmp);
  const all = visible.length === branches.length;
  const title = !branches.length
    ? "All branches"
    : all
      ? "All branches"
      : visible.length <= 3
        ? visible.map((x) => x.n).join(" · ")
        : `${visible.length} of ${branches.length} branches`;
  const sub = !branches.length
    ? "No branches are assigned to this user"
    : period === "today"
      ? `${longDate(r.to)} · consolidated across ${visible.length} branches`
      : `${cap(r.name)} · ${r.n === 1 ? dayLabel(r.to) : dayLabel(r.from) + " → " + dayLabel(r.to)} · ${visible.length} branches`;

  const best = league[0];
  const maxShare = league.reduce((m, x) => Math.max(m, x.share), 1);
  const shown = (trend?.series || []).filter((s) => !hidden[s.n]);
  const shareRows = v ? v.rows.slice().sort((a, b) => b.sales - a.sales) : [];

  // GetSummary tiles; no delta while the period is live (whole comparison days)
  const sv = (x) => (v?.hasSum ? compact(x || 0) : "—");
  const sdelta = (cur, prev) => (v?.hasSum && !v.r.live ? <Delta cur={cur} prev={prev} small /> : null);

  // needs a decision
  const idle = v ? v.rows.filter((x) => !x.inv) : [];
  const falling = v ? v.rows.filter((x) => x.prevSales && x.growth <= -10) : [];
  const pendingBills = v ? v.rows.flatMap((x) => x.cur.filter((i) => i.status === 1).map((i) => ({ ...i, br: x.n }))) : [];

  // GetSummary / GetPosition / GetList alerts
  const P = v?.pos || {};
  const posRows = v ? v.rows.filter((x) => x.p) : [];
  const underTarget = v ? v.rows.filter((x) => x.target && x.tgt < 85).sort((a, b) => a.tgt - b.tgt) : [];
  const notClosed = posRows.filter((x) => x.p.businessDate && !x.p.dayClosedUpToYesterday);
  const lossOf = (x) => (Number(x.p.damageValue30) || 0) + (Number(x.p.wastageValue30) || 0);
  const lossAvg = posRows.length ? sumOf(posRows, lossOf) / posRows.length : 0;
  const highLoss = posRows.filter((x) => lossOf(x) > 0 && lossOf(x) > lossAvg);
  const transit = (P.transfersInTransitIn || 0) + (P.transfersInTransitOut || 0);
  const noPos = v && !v.hasPos ? "could not be loaded" : null;
  const serverQueue = !v
    ? []
    : [
        {
          key: "target",
          sev: underTarget.length ? "crit" : "info",
          ic: "chart",
          t: underTarget.length ? "Branches under 85% of target" : v.withTarget.length ? "Every branch is at 85% of target or better" : "No branch targets set",
          s: underTarget.length ? underTarget.map((x) => `${x.n} ${fmtPct(x.tgt, 0)}`).join(", ") : v.withTarget.length ? `${r.name}, to date` : "open Sales targets to set them",
          n: underTarget.length ? `${underTarget.length} of ${v.withTarget.length}` : v.withTarget.length ? "All clear" : "—",
        },
        {
          key: "ar90",
          kind: P.customersOver90 ? "ar90" : null,
          sev: P.customersOver90 ? "crit" : "info",
          ic: "clock",
          t: "Receivables past 90 days, group-wide",
          s: noPos || `${compact(P.receivableOver90 || 0)} overdue`,
          n: P.customersOver90 ? `${money(P.customersOver90)} customers` : noPos ? "—" : "All clear",
        },
        {
          key: "transfer",
          kind: transit ? "transfers" : null,
          sev: transit ? "warnx" : "info",
          ic: "truck",
          t: "Stock transfers pending acceptance",
          s: noPos || (transit ? `oldest ${P.oldestTransitDays ?? 0} days · stock is in limbo until accepted` : "nothing in transit"),
          n: transit ? `${money(P.transfersInTransitIn || 0)} notes` : noPos ? "—" : "All clear",
        },
        {
          key: "price",
          cached: "price",
          sev: data.price.length ? "warnx" : "info",
          ic: "tag",
          t: "Same item priced differently across branches",
          s: "spread over 5%, last 30 days' selling prices",
          n: data.price.length ? `${money(data.price.length)} items` : "All clear",
        },
        {
          key: "dayclose",
          kind: notClosed.length ? "dayclose" : null,
          sev: notClosed.length ? "warnx" : "info",
          ic: "lock",
          t: notClosed.length ? "Branches that have not closed the day" : "Every POS branch closed yesterday",
          s: noPos || (notClosed.length ? notClosed.map((x) => x.n).join(", ") : "cash and stock reconciled"),
          n: notClosed.length ? `${notClosed.length} of ${posRows.filter((x) => x.p.businessDate).length}` : noPos ? "—" : "All clear",
        },
        {
          key: "misplaced",
          cached: "misplaced",
          sev: data.misplaced.length ? "warnx" : "info",
          ic: "box",
          t: "Stock sitting in the wrong branch",
          s: "out of stock here, idle there",
          n: data.misplaced.length ? `${money(data.misplaced.length)} items` : "All clear",
        },
        {
          key: "credit",
          kind: P.customersOverLimit ? "credit" : null,
          sev: "info",
          ic: "card",
          t: "Customers over their credit limit",
          s: noPos || `${compact(P.overLimitAmount || 0)} over the limits`,
          n: P.customersOverLimit ? `${money(P.customersOverLimit)} customers` : noPos ? "—" : "All clear",
        },
        {
          key: "wast",
          kind: highLoss.length ? "wastage" : null,
          sev: "info",
          ic: "box",
          t: "Damage & wastage above the group average",
          s: noPos || (highLoss.length ? highLoss.map((x) => `${x.n} ${compact(lossOf(x))}`).join(", ") : "last 30 days"),
          n: highLoss.length ? `${highLoss.length} of ${posRows.length}` : noPos ? "—" : "All clear",
        },
      ];
  const queue = [
    {
      key: "idle",
      sev: idle.length ? "crit" : "info",
      ic: "chart",
      t: idle.length ? `${idle.length} ${idle.length === 1 ? "branch has" : "branches have"} no sales ${r.name}` : `Every branch is trading ${r.name}`,
      s: idle.length ? idle.map((x) => x.n).join(", ") : "nothing to chase",
      n: idle.length ? `${idle.length} of ${visible.length}` : "All clear",
    },
    {
      key: "falling",
      sev: falling.length ? "warnx" : "info",
      ic: "chart",
      t: falling.length ? "Branches down 10% or more" : "No branch is down 10% or more",
      s: falling.length ? falling.map((x) => `${x.n} ${fmtPct(x.growth, 0)}`).join(", ") : `vs ${c.name}`,
      n: falling.length ? `${falling.length} of ${visible.length}` : "All clear",
    },
    {
      key: "pending",
      sev: pendingBills.length ? "warnx" : "info",
      ic: "doc",
      t: pendingBills.length ? "Bills still pending, group-wide" : "No pending bills",
      s: "raised but not completed",
      n: pendingBills.length ? `${pendingBills.length} bills` : "All clear",
    },
    ...serverQueue,
  ];
  const onQueue = (x) => {
    if (x.cached && data[x.cached].length) setDrawer(listDrawer(x.cached, data[x.cached]));
    else if (x.kind) openList(x.kind);
    else if (x.key === "target" && underTarget.length) openBranch(underTarget[0]);
    else if (x.key === "target") navigate(all_routes.salestarget);
    else if (x.key === "pending") openInGroupRows("Bills still pending", pendingBills);
    else if (x.key === "idle" && idle.length) showToast(`No invoices from ${idle.map((b) => b.n).join(", ")}`);
    else if (x.key === "falling" && falling.length) openBranch(falling[0]);
  };

  const drawerEl = (
    <Drawer
      data={drawer}
      onClose={() => setDrawer(null)}
      onCsv={drawerCsv}
      primary={
        drawer?.guid ? (
          <button type="button" className="axd-btn primary" onClick={() => goBranch(drawer.guid)}>
            Open branch dashboard
          </button>
        ) : (
          <button type="button" className="axd-btn primary" onClick={() => setDrawer(null)}>
            Done
          </button>
        )
      }
    />
  );

  return (
    <DashScreen
      title={title}
      sub={sub}
      updated={updated}
      loading={loading}
      error={error}
      drawer={drawerEl}
      toast={toast}
      keys={[
        ["T", "Today"],
        ["W", "Week"],
        ["M", "Month"],
        ["A", "All branches"],
        ["R", "Refresh"],
        ["Esc", "Close"],
      ]}
      status={`${visible.length} of ${branches.length} branches · ${vatOn ? "incl. VAT" : "excl. VAT"} · ${r.n} ${r.n === 1 ? "day" : "days"}`}
    >
      <PeriodBar
        periods={PERIODS}
        period={period}
        onPeriod={setPeriod}
        cmp={cmp}
        onCmp={setCmp}
        vat={vatOn}
        onVat={setVatOn}
        vatLabel="With VAT"
        onRefresh={refresh}
        onCsv={exportCsv}
        onPrint={() => window.print()}
      />

      <div className="axd-scope">
        <span className="lb">Branches</span>
        {branches.map((x) => (
          <button
            type="button"
            key={x.b.guid}
            className={"axd-bchip" + (off[x.b.guid] ? "" : " on")}
            onClick={() => toggleBranch(x.b.guid)}
          >
            <span className="sw" style={{ background: x.c }} />
            {x.n}
          </button>
        ))}
        <button type="button" className="axd-btn" onClick={allBranches}>
          All
        </button>
      </div>

      {/* ============ KPI ROW ============ */}
      <section className="axd-kpirow">
        <Hero
          label={`${all ? "Group net sales" : `${visible.length} branches`} · ${r.name}`}
          value={money(v?.total || 0)}
          loading={!v && loading}
          prev={v ? { cur: v.total, prev: v.prevTotal } : undefined}
          cmpName={c.name}
          spark={<Sparkline vals={v?.sparkGroup || []} w={256} h={40} />}
          footLabel={r.live ? "Target to now" : "Group target"}
          foot={<b>{v?.target ? money(v.target) : "—"}</b>}
          footRight={
            !v ? null : v.target ? (
              <b style={{ color: v.achieved >= v.target ? "var(--money)" : undefined }}>{fmtPct(pct(v.achieved, v.target), 0)} achieved</b>
            ) : (
              v.hasSum && "no targets set"
            )
          }
        />
        <div className="axd-tiles">
          <Tile
            label="Gross profit"
            value={sv(v?.gp)}
            delta={sdelta(v?.gp, v?.pgp)}
            sub={`margin ${fmtPct(pct(v?.gp || 0, v?.salesEx || 0))} · ex VAT`}
            spark={<Sparkline vals={v?.sparkGp || []} />}
            onClick={() => setRank("gpPct")}
          />
          <Tile
            label="Invoices"
            value={money(v?.inv || 0)}
            delta={v ? <Delta cur={v.inv} prev={v.pinv} small /> : null}
            sub={`avg bill ${money(v && v.inv ? v.total / v.inv : 0)}`}
            spark={<Sparkline vals={v?.sparkInv || []} />}
            onClick={() =>
              v &&
              openInGroupRows(
                `Invoices · ${r.name}`,
                v.rows.flatMap((x) => x.cur.map((i) => ({ ...i, br: x.n })))
              )
            }
          />
          <Tile
            label="Collections"
            value={sv(v?.coll)}
            delta={sdelta(v?.coll, v?.pcoll)}
            sub="cash & card in, incl. receipts"
            spark={<Sparkline vals={v?.sparkColl || []} />}
            onClick={() => setRank("coll")}
          />
          <Tile
            label="Purchases"
            value={sv(v?.purch)}
            delta={sdelta(v?.purch, v?.ppurch)}
            sub={`net of returns · paid ${compact(sumOf(v?.rows || [], (x) => sumOf(inRange(x.sdays, r.from, r.to), (d) => d.paidToSuppliers)))}`}
            spark={<Sparkline vals={v?.sparkPurch || []} />}
            onClick={() =>
              v &&
              setDrawer({
                title: `Purchases · ${r.name}`,
                sub: `${visible.length} branches`,
                cols: [
                  { k: "n", t: "Branch" },
                  { k: "p", t: "Purchases (net)", num: true },
                  { k: "s", t: "Paid to suppliers", num: true },
                  { k: "o", t: "We owe (now)", num: true },
                ],
                rows: v.rows.map((x) => ({
                  n: x.n,
                  p: moneyDp(x.purch),
                  s: moneyDp(sumOf(inRange(x.sdays, r.from, r.to), (d) => d.paidToSuppliers)),
                  o: x.p ? moneyDp(x.p.payableTotal) : "—",
                })),
              })
            }
          />
          <Tile
            label="Receivables"
            value={v?.hasPos ? compact(P.receivableTotal || 0) : "—"}
            sub={v?.hasPos ? `${compact(P.receivableOver90 || 0)} over 90 days` : "could not be loaded"}
            onClick={() => openList("receivables")}
          />
          <Tile
            label="Branches on target"
            value={v?.withTarget.length ? `${v.onTarget.length} / ${v.withTarget.length}` : "—"}
            sub={v?.withTarget.length ? `${r.name}, to date` : best ? `no targets set · best: ${best.n}` : "no targets set"}
            onClick={() => setRank("tgt")}
          />
        </div>
      </section>

      <section className="axd-g12">
        {/* LEAGUE TABLE */}
        <Card
          className="c12"
          title="Branch league"
          note={v ? `${league.length} branches · ${cap(r.name)} vs ${c.name}` : ""}
          right={
            <>
              <span className="note" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                rank by
              </span>
              <Seg small items={RANKS} value={rank} onChange={setRank} />
            </>
          }
        >
          <div className="axd-lwrap">
            <table className="axd-lg">
              <thead>
                <tr>
                  {LCOLS.map((col) => (
                    <th key={col.k} className={col.num ? "num" : undefined} style={col.w ? { width: col.w } : undefined}>
                      {SORTABLE.includes(col.k) ? (
                        <button type="button" className="h" onClick={() => setRank(col.k)}>
                          {col.t}
                          {rank === col.k ? <span style={{ color: "var(--accent)" }}>▾</span> : null}
                        </button>
                      ) : (
                        <span className="h">{col.t}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {league.map((x) => (
                  <tr key={x.b.guid} onClick={() => openBranch(x)}>
                    <td>
                      <span className={"axd-rk " + (x.rank === 1 ? "top" : x.rank === league.length && league.length > 2 ? "bot" : "")}>
                        {x.rank}
                      </span>
                    </td>
                    <td>
                      <div className="axd-bcell">
                        <span className="sw" style={{ background: x.c }} />
                        <div>
                          <b>{x.n}</b>
                          <span className="axd-sharebar">
                            <i style={{ width: pct(x.share, maxShare).toFixed(1) + "%", background: x.c }} />
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="num">
                      <b>{money(x.sales)}</b>
                      <small>{fmtPct(x.share)} of group</small>
                    </td>
                    <td className="num">
                      <Delta cur={x.sales} prev={x.prevSales} small />
                    </td>
                    {v.hasSum ? <td className="num">{fmtPct(pct(x.gp, x.salesEx))}</td> : <td className="num na">—</td>}
                    <td className="num">{money(x.inv)}</td>
                    <td className="num">{money(x.avg)}</td>
                    {v.hasSum ? <td className="num">{money(x.coll)}</td> : <td className="num na">—</td>}
                    {x.p ? (
                      <td className="num">
                        {money(x.ar)}
                        {Number(x.p.receivableOver90) > 0 && <small style={{ color: "var(--warn)" }}>{compact(x.p.receivableOver90)} over 90d</small>}
                      </td>
                    ) : (
                      <td className="num na">—</td>
                    )}
                    {x.p ? <td className="num">{money(x.stock)}</td> : <td className="num na">—</td>}
                    {x.target ? (
                      <td className="num">
                        <b style={{ color: x.tgt >= 100 ? "var(--money)" : x.tgt < 85 ? "var(--danger)" : undefined }}>{fmtPct(x.tgt, 0)}</b>
                        <small>of {compact(x.target)}</small>
                      </td>
                    ) : (
                      <td className="num na">—</td>
                    )}
                    <td className="num">
                      <MicroSpark vals={x.spark} color={x.c} />
                    </td>
                  </tr>
                ))}
                {!league.length && (
                  <tr>
                    <td colSpan={LCOLS.length} className="na" style={{ textAlign: "center" }}>
                      {loading ? "Loading…" : branches.length ? "No figures yet." : "No branches are assigned to this user."}
                    </td>
                  </tr>
                )}
              </tbody>
              {v && league.length > 0 && (
                <tfoot>
                  <tr>
                    <td />
                    <td>Group total</td>
                    <td className="num">{money(v.total)}</td>
                    <td className="num">
                      <Delta cur={v.total} prev={v.prevTotal} small />
                    </td>
                    <td className="num">{v.hasSum ? fmtPct(pct(v.gp, v.salesEx)) : ""}</td>
                    <td className="num">{money(v.inv)}</td>
                    <td className="num">{money(v.inv ? v.total / v.inv : 0)}</td>
                    <td className="num">{v.hasSum ? money(v.coll) : ""}</td>
                    <td className="num">{v.hasPos ? money(sumOf(v.rows, (x) => x.ar)) : ""}</td>
                    <td className="num">{v.hasPos ? money(sumOf(v.rows, (x) => x.stock)) : ""}</td>
                    <td className="num">{v.target ? fmtPct(pct(v.achieved, v.target), 0) : ""}</td>
                    <td className="num" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        {/* TREND */}
        <Card
          className="c8"
          title={trend?.unit === "hour" ? "Sales by branch, through the day" : "Sales by branch, day by day"}
          note={
            trend
              ? trend.noTimes
                ? "last 12 days · no hourly figures"
                : `${shown.length} of ${trend.series.length} branches shown${trend.fromSummary ? " · after returns" : ""} · click a name to hide it`
              : ""
          }
          right={
            <MiniBtn on={tables.trend} onClick={() => toggleTable("trend")}>
              {tables.trend ? "Chart" : "Table"}
            </MiniBtn>
          }
          bodyClass="col"
        >
          {!trend ? (
            <div className="axd-nochart axd-empty">{loading ? "Loading…" : "No figures yet."}</div>
          ) : tables.trend ? (
            <DataTable
              cols={[
                { k: "x", t: trend.unit === "hour" ? "Hour" : "Date" },
                ...trend.series.map((s) => ({ k: s.n, t: s.n, num: true })),
                { k: "__g", t: "Group", num: true },
              ]}
              rows={trend.x.map((s, i) => {
                const row = { x: s, __g: <b>{money(sumOf(trend.series, (se) => se.v[i] || 0))}</b> };
                trend.series.forEach((se) => (row[se.n] = money(se.v[i] || 0)));
                return row;
              })}
            />
          ) : (
            <LineTrend x={trend.x} series={shown} />
          )}
          {trend && (
            <div className="axd-legend chips">
              {trend.series.map((s) => (
                <button type="button" key={s.n} className={"axd-lgbtn" + (hidden[s.n] ? " off" : "")} onClick={() => toggleSeries(s.n)}>
                  <span className="ln" style={{ background: s.c }} />
                  {s.n}
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* CONTRIBUTION */}
        <Card
          className="c4"
          title="Share of group sales"
          right={
            <MiniBtn on={tables.share} onClick={() => toggleTable("share")}>
              {tables.share ? "Chart" : "Table"}
            </MiniBtn>
          }
        >
          {tables.share ? (
            <DataTable
              cols={[
                { k: "n", t: "Branch" },
                { k: "v", t: "Net sales", num: true },
                { k: "p", t: "Share", num: true },
              ]}
              rows={[
                ...shareRows.map((x) => ({ n: x.n, v: moneyDp(x.sales), p: fmtPct(x.share) })),
                { __total: true, n: "Group", v: moneyDp(v?.total || 0), p: "100.0%" },
              ]}
            />
          ) : (
            <>
              <StackBar parts={shareRows.map((x) => ({ n: x.n, v: x.sales, c: x.c }))} />
              <div className="axd-stkey one">
                {shareRows.map((x) => (
                  <span className="k" key={x.b.guid}>
                    <span className="sw" style={{ background: x.c }} />
                    <span className="kn">{x.n}</span>
                    <span className="kv">{money(x.sales)}</span>
                    <span className="kp">{fmtPct(x.share)}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* STOCK TRANSFERS */}
        <Card
          className="c5"
          title="Stock moving between branches"
          note="in transit until the receiver accepts"
          right={
            <MiniBtn onClick={() => openList("transfers")} title="Transfers in transit">
              List
            </MiniBtn>
          }
          bodyClass="col"
        >
          {v?.hasPos ? (
            <>
              <Facts
                cols={3}
                items={[
                  { l: "In transit", v: money(P.transfersInTransitIn || 0), s: "notes not yet accepted", cls: P.transfersInTransitIn ? "warn" : "" },
                  { l: "Value", v: compact(P.transfersInTransitInValue || 0), s: "stock in limbo" },
                  { l: "Oldest", v: P.oldestTransitDays != null ? `${P.oldestTransitDays} days` : "—", s: "waiting for acceptance" },
                ]}
              />
              <DataTable
                cols={[
                  { k: "n", t: "Branch" },
                  { k: "o", t: "Sent, in transit", num: true },
                  { k: "i", t: "To accept", num: true },
                ]}
                rows={posRows
                  .filter((x) => x.p.transfersInTransitOut || x.p.transfersInTransitIn)
                  .map((x) => ({
                    n: x.n,
                    o: `${money(x.p.transfersInTransitOut)} · ${compact(x.p.transfersInTransitOutValue)}`,
                    i: `${money(x.p.transfersInTransitIn)} · ${compact(x.p.transfersInTransitInValue)}`,
                  }))}
              />
            </>
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Transfers could not be loaded."}</div>
          )}
        </Card>

        {/* EXCEPTIONS */}
        <Card className="c4" title="Needs a decision" note={`${queue.filter((x) => x.sev !== "info").length} need action`}>
          <Queue items={queue} onPick={onQueue} />
        </Card>

        {/* GROUP STOCK */}
        <Card
          className="c3"
          title="Group stock"
          right={
            <MiniBtn onClick={() => openList("deadstock")} title="Items with no sale in 90 days">
              Dead
            </MiniBtn>
          }
        >
          {v?.hasPos ? (
            <Facts
              cols={1}
              items={[
                { l: "Stock value", v: compact(P.stockValue || 0), s: `${money(P.itemsInStock || 0)} item lines in stock` },
                { l: "Cover", v: P.stockCoverDays ? `${Math.round(P.stockCoverDays)} days` : "—", s: "at the last 30 days' pace" },
                { l: "Dead stock", v: compact(P.deadStockValue || 0), s: `${money(P.deadStockItems || 0)} items, no sale in 90 days` },
                { l: "Negative stock", v: money(P.negativeItems || 0), s: "item lines below zero", cls: P.negativeItems ? "bad" : "" },
              ]}
            />
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Stock figures could not be loaded."}</div>
          )}
        </Card>

        {/* RECEIVABLES BY BRANCH */}
        <Card
          className="c6"
          title="Money owed to us · by branch"
          note={v?.hasPos ? `${compact(P.receivableTotal || 0)} as on today` : ""}
        >
          {v?.hasPos ? (
            <div className="axd-blist">
              {posRows
                .slice()
                .sort((a, b) => b.ar - a.ar)
                .map((x) => (
                  <button type="button" className="axd-brow" key={x.b.guid} onClick={() => openList("receivables", x)}>
                    <span>
                      <span className="nm">{x.n}</span>
                      <StackBar thin parts={ageParts(x.p)} />
                    </span>
                    <span className="v">
                      {compact(x.ar)}
                      <small className={Number(x.p.receivableOver90) > 0 ? "warn" : undefined}>
                        {Number(x.p.receivableOver90) > 0 ? `${compact(x.p.receivableOver90)} over 90d` : "none over 90d"}
                      </small>
                    </span>
                  </button>
                ))}
            </div>
          ) : (
            <div className="axd-empty">{loading ? "Loading…" : "Receivables could not be loaded."}</div>
          )}
          <div className="axd-legend chips">
            {["Not due / 0–30 days", "31–60 days", "61–90 days", "Over 90 days"].map((n, i) => (
              <span className="axd-lgbtn" key={n}>
                <span className="ln" style={{ height: 10, width: 10, borderRadius: 3, background: `var(--age${i + 1})` }} />
                {n}
              </span>
            ))}
          </div>
        </Card>

        {/* GROUP TOP ITEMS */}
        <Card className="c6" title="Top items across the group" note="and how widely they sell">
          <div className="axd-blist">
            {topItems.length ? (
              topItems.map((x) => {
                const narrow = x.by.length < Math.max(2, Math.ceil(visible.length * 0.6));
                return (
                  <button
                    type="button"
                    className="axd-brow"
                    key={x.name}
                    onClick={() =>
                      setDrawer({
                        title: x.name,
                        sub: `${cap(r.name)} · branch by branch`,
                        cols: [
                          { k: "n", t: "Branch" },
                          { k: "q", t: "Qty", num: true },
                          { k: "v", t: "Value", num: true },
                        ],
                        rows: x.by
                          .slice()
                          .sort((a, b) => b.value - a.value)
                          .map((b) => ({ n: b.n, q: money(b.qty), v: moneyDp(b.value) })),
                      })
                    }
                  >
                    <span>
                      <span className="nm">{x.name}</span>
                      <span className="track">
                        <i style={{ width: pct(x.value, topItems[0].value).toFixed(1) + "%" }} />
                      </span>
                    </span>
                    <span className="v">
                      {compact(x.value)}
                      <small className={narrow ? "warn" : undefined}>
                        sells in {x.by.length}/{visible.length}
                      </small>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="axd-empty">{loading ? "Loading…" : "No item sales in this period."}</div>
            )}
          </div>
        </Card>
      </section>
    </DashScreen>
  );
};

export default GroupDashboard;
